import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {useHistory, useLocation} from 'react-router-dom'
import {loadReviewSummary} from '../../api'
import {
  IApplicationFormData,
  IWizardReviewSummary,
  normalizeApplicationFormData,
} from '../../models/application-form'
import WizardSectionCard from './sections/WizardSectionCard'
import {applyCardReadinessToReviewItems, buildMandatoryReviewItems} from './reviewChecklist'

interface ReviewStepProps {
  register: UseFormRegister<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
}

const ReviewStep: React.FC<ReviewStepProps> = ({watch, setValue}) => {
  const history = useHistory()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const requestVersion = useRef(0)
  const [lastSummary, setLastSummary] = useState<{
    sessionId: string
    summary: IWizardReviewSummary
  } | null>(null)
  const formData = normalizeApplicationFormData(watch())
  const applicationId = formData.applicationId
  const sessionId = formData.sessionId
  const cachedSummary =
    lastSummary && lastSummary.sessionId === sessionId ? lastSummary.summary : undefined
  const cardReadiness = cachedSummary?.cardReadiness ?? formData.reviewSummary?.cardReadiness
  const mandatoryChecklist = useMemo(
    () =>
      applyCardReadinessToReviewItems(
        buildMandatoryReviewItems({
          ...formData,
          reviewSummary: cachedSummary ?? formData.reviewSummary,
        }),
        cardReadiness
      ),
    [formData, cachedSummary, cardReadiness]
  )
  const canFinalize = (cardReadiness?.canFinalize ?? cardReadiness?.isComplete) === true
  const readinessMessage = cardReadiness?.missingRequirementMessages[0]

  const refreshReviewStatus = useCallback(async () => {
    const version = ++requestVersion.current
    if (!sessionId) {
      setLoadError('Session expired. Please verify your mobile again.')
      setLoading(false)
      return
    }

    setLoading(true)
    setLoadError(null)
    // Invalidate the form's submit decision while retaining the last known
    // summary for display. A failed refresh must not enable finalization.
    setValue('reviewSummary.cardReadiness', undefined, {shouldDirty: false})

    try {
      const summaryPayload = await loadReviewSummary(sessionId)
      if (version !== requestVersion.current) return
      if (!summaryPayload.cardReadiness?.documentReadiness) {
        throw new Error('Card readiness is unavailable.')
      }
      setLastSummary({sessionId, summary: summaryPayload})
      setValue('reviewSummary', summaryPayload, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    } catch {
      if (version === requestVersion.current) {
        setLoadError('Unable to refresh your saved Doora Card status. Please try again.')
      }
    } finally {
      if (version === requestVersion.current) setLoading(false)
    }
  }, [sessionId, setValue])

  useEffect(() => {
    refreshReviewStatus()
    return () => {
      requestVersion.current++
    }
  }, [applicationId, formData.lastSavedAtUtc, refreshReviewStatus])

  const getWizardScrollTarget = (itemKey: string): string | undefined => {
    switch (itemKey) {
      case 'proof-of-employment':
        return 'proof-of-employment'
      case 'proof-of-benefits':
        return 'proof-of-benefits'
      case 'rental-history':
        return 'rental-ledger'
      default:
        return undefined
    }
  }

  const jumpToStep = (stepPath: string, wizardScrollTarget?: string) => {
    history.push({
      pathname: `/application-wizard/${stepPath}`,
      search: location.search,
      state: wizardScrollTarget ? {wizardScrollTarget} : undefined,
    })
  }

  return (
    <div>
      <div className='wizard-hint-card review black mb-8'>
        {loading || loadError ? (
          <>
            <h4>{loading ? 'Refreshing your Doora Card' : 'Doora Card status unavailable'}</h4>
            <div className='subtitle'>
              {loadError ?? 'Checking your saved details and documents.'}
            </div>
          </>
        ) : !canFinalize ? (
          <>
            <h4>
              Your <span className='doora-text'>Doora Card</span> needs attention.
            </h4>
            <div className='subtitle'>
              {readinessMessage ?? 'Refreshing your saved Doora Card requirements.'}
            </div>
          </>
        ) : (
          <>
            <h4>
              Your <span className='doora-text'>Doora Card</span> is{' '}
              {cardReadiness?.isComplete ? 'ready.' : 'ready to finalise.'}
            </h4>
            <div className='subtitle'>
              {cardReadiness?.documentReadiness?.deferredTypes?.length
                ? 'You can finalise now and upload the documents marked "Upload later" afterwards.'
                : 'Send it to any property in Australia.'}
            </div>
          </>
        )}
      </div>

      <WizardSectionCard title="What's in your card" className='p-8 mb-8 wizard-review-card'>
        <div className='d-flex justify-content-end mb-3'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={refreshReviewStatus}
            disabled={loading}
            aria-label='Refresh Doora Card status'
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
            {loadError ? ' Retry' : ' Refresh'}
          </button>
        </div>

        <ul className='wizard-review-list wizard-review-modern-list mb-0 mt-5'>
          {mandatoryChecklist.map((item) => (
            <li
              key={item.key}
              className={`wizard-review-item wizard-review-modern-row ${
                item.complete && !item.pendingUpload ? 'ok' : 'pending'
              }`}
            >
              <div className='wizard-review-item-row wizard-review-modern-row-inner'>
                <div className='wizard-review-modern-main'>
                  <span
                    className={`wizard-review-modern-icon ${
                      item.complete && !item.pendingUpload
                        ? 'wizard-review-modern-icon-complete'
                        : 'wizard-review-modern-icon-pending'
                    }`}
                  >
                    <i
                      className={`bi ${
                        item.complete === undefined
                          ? 'bi-dash'
                          : item.pendingUpload
                          ? 'bi-clock'
                          : item.complete
                          ? 'bi-check'
                          : 'bi-x'
                      }`}
                      aria-label={
                        item.complete === undefined
                          ? 'Status unavailable'
                          : item.pendingUpload
                          ? 'Upload later'
                          : item.complete
                          ? 'Complete'
                          : 'Incomplete'
                      }
                    ></i>
                  </span>
                  <span className='wizard-review-modern-label'>{item.label}</span>
                </div>

                <div className='wizard-review-actions ms-3 me-10'>
                  <span className='text-muted'>{item.retentionType}</span>
                  {item.pendingUpload && <span className='text-muted me-3'>Upload later</span>}
                  {item.canNavigate !== false && item.stepPath && (
                    <button
                      type='button'
                      className='btn btn-link p-0 wizard-review-modern-link'
                      onClick={() => jumpToStep(item.stepPath, getWizardScrollTarget(item.key))}
                    >
                      {item.complete ? 'Edit' : 'Edit'}
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        {!loading && !loadError && !canFinalize && (
          <div className='text-muted mt-4'>
            Complete all mandatory checklist items to enable Finalise your Doora Card.
          </div>
        )}
      </WizardSectionCard>

      {loading && <div className='text-muted'>Refreshing review summary...</div>}
      {loadError && <div className='text-danger'>{loadError}</div>}
    </div>
  )
}

export default ReviewStep
